import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	ArrowLeftIcon,
	Button,
	DialogueIcon,
	FlexGap,
	FormErrorMessage,
	GraphQLErrorMessage,
	Heading3,
	IconButton,
	Input,
	Modal,
	SendIcon,
	tokens,
} from '@treams/treams-ui';

import RecipientSelector from '../../../../kakapo-ui-kit/components/recipients/RecipientSelector';
import { Recipient, isUserRecipient } from '../../../../kakapo-ui-services/models/Recipient';
import FeedbackRequestExternalConfirmModal from './FeedbackRequestExternalConfirmModal';
import FeedbackRequestProgress from './FeedbackRequestProgress';

export default function FeedbackRequestModalRecipients(props: {
	returnURL: string;
	error?: any;
	recipients: Recipient[];
	onBack: () => void;
	onChange: (recipients: Recipient[]) => void;
	onDone: (
		dueDate: Date,
		internals: string[],
		externals: graphql.FeedbackExternal[],
	) => void | Promise<void>;
	feedbackTemplateID?: string;
	minimumRecipients?: number;
	maximumRecipients?: number;
	releaseDate?: Date | string | null;
}) {
	const { t } = useTranslation();

	const [error, setError] = useState<Error | undefined>(undefined);
	const [showConfirm, setShowConfirm] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isSubmittingRef = useRef(false);
	const nextWeek = new Date(Date.now() + 7 * 24 * 3600 * 1000);
	const [dueDate, setDueDate] = useState(nextWeek);

	useEffect(() => {
		if (props.error) {
			setShowConfirm(false);
		}
	}, [props.error]);

	const internals = props.recipients.filter(isUserRecipient).map((recipient) => recipient.userId);

	const externalRecipients = props.recipients.filter(
		(recipient): recipient is Extract<Recipient, { name: string; email: string }> =>
			!isUserRecipient(recipient),
	);

	const externals = externalRecipients.map(
		(recipient) =>
			({
				firstName: recipient.name.split(' ')[0],
				lastName: recipient.name.split(' ').slice(1).join(' '),
				email: recipient.email,
			}) as graphql.FeedbackExternal,
	);

	const handleSend = async () => {
		if (isSubmittingRef.current) {
			return;
		}

		isSubmittingRef.current = true;
		setIsSubmitting(true);
		setShowConfirm(false);

		try {
			await props.onDone(dueDate, internals, externals);
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	const submit = () => {
		setError(undefined);

		if (props.recipients.length < (props.minimumRecipients || 1)) {
			setError(
				new Error(
					t('common.modules.feedback.minimumGivers', {
						count: props.minimumRecipients || 1,
					}),
				),
			);
			return;
		}

		if (!!props.maximumRecipients && props.recipients.length > props.maximumRecipients) {
			setError(
				new Error(
					t('common.modules.feedback.maximumGivers', {
						count: props.maximumRecipients,
					}),
				),
			);
			return;
		}

		if (externalRecipients.length > 0) {
			setShowConfirm(true);
			return;
		}

		void handleSend();
	};

	return (
		<>
			<Modal width="704px">
				<Modal.Header
					leftAccessory={
						<IconButton icon={<ArrowLeftIcon />} type="secondary" onClick={props.onBack} />
					}
					icon={<DialogueIcon size={24} />}
					title={t('common.feedback.selectRecipients')}
					closeLink={props.returnURL}
				/>

				<FeedbackRequestProgress step={2} />
				<Modal.Contents pt={0}>
					<FlexGap flexDirection="column" gap={tokens.gap.group}>
						{props.error && <GraphQLErrorMessage error={props.error} />}
						{error && <FormErrorMessage error={error} />}
						<RecipientSelector
							initialRecipients={props.recipients}
							onChange={props.onChange}
							footer={
								<FlexGap flexDirection="column" gap={tokens.gap.group}>
									<Heading3>{t('dueDate.selector.label')}</Heading3>
									<Box width={160}>
										<Input.DatePicker
											value={dueDate}
											onChange={(value) => setDueDate(value ?? nextWeek)}
										/>
									</Box>
									{props.releaseDate && (
										<FlexGap flexDirection="column" gap={tokens.gap.group}>
											<Heading3>{t('feedback.request.releaseDate.description')}</Heading3>
											<Box width={160}>
												<Input.DatePicker
													disabled
													value={new Date(props.releaseDate)}
													onChange={() => {}}
												/>
											</Box>
										</FlexGap>
									)}
								</FlexGap>
							}
						/>
					</FlexGap>
				</Modal.Contents>
				<Modal.Footer>
					<Button type="secondary" title={t('modal.footer.form.cancel')} onClick={props.onBack} />
					<Button
						type="primary"
						icon={<SendIcon />}
						title={t('common.feedback.nextStepSend')}
						disabled={dueDate <= new Date() || isSubmitting}
						onClick={submit}
					/>
				</Modal.Footer>
			</Modal>
			{showConfirm && (
				<FeedbackRequestExternalConfirmModal
					externals={externalRecipients}
					internalCount={internals.length}
					isSubmitting={isSubmitting}
					onAdjust={() => setShowConfirm(false)}
					onConfirm={() => void handleSend()}
				/>
			)}
		</>
	);
}
